/**
 * Life Journey Filter Component
 * Version: 1.5.0
 * Last Updated: 2026-03-18
 *
 * Changelog:
 * - v1.5.0: Multi-tag category matching. Articles/products now appear under ALL
 *           categories that match their tags array, not just their primary category.
 *           Added normalizeTagTitle() for case-insensitive & punctuation-tolerant
 *           tag-to-category resolution. Added byTitleNormalized to categoryMap.
 *           Added itemMatchesSelectedCategories() helper.
 *           displaySliderResults now groups items into every matching category slot.
 * - v1.4.0: Added "Recommended" as default sort option (preserves API return order).
 *           Dynamic sort_field/sort_direction mapping in API requests based on user selection.
 *           Removed groupByParticipant to preserve API result ordering.
 *           Added combined array from transformAPIResponse to maintain exact API order.
 * - v1.3.0: Added preview_image_url as primary image source with fallback chain
 *           (preview_image_url > featured_image/image_url > category fallback)
 * - v1.2.2: Code cleanup - removed debug console.log statements
 * - v1.2.1: Added secondary image check via Shopify JSON API for offerings when API returns null
 * - v1.2.0: Added normalized matching for company names (handles apostrophes, plural/singular)
 * - v1.1.9: Match author name to participants_profile to get company_logo from preloaded data
 * - v1.1.0: Added support for author/company logo images with configurable default placeholder
 */

(function () {
  "use strict";

  const FAVORITES_ORGANIZATION = ["Homesteaders Life Company"];

  /**
   * v1.4.0: Sort-to-API parameter mapping
   * Maps frontend sort selection to API sort_field and sort_direction
   */
  const SORT_API_MAP = {
    recommended: { sort_field: null, sort_direction: "desc" },
    newest:      { sort_field: "createdAt", sort_direction: "desc" },
    oldest:      { sort_field: "createdAt", sort_direction: "asc" },
    "a-z":       { sort_field: "title",     sort_direction: "asc" },
    "z-a":       { sort_field: "title",     sort_direction: "desc" }
  };

  /**
   * v1.4.0: Sort label mapping (used in desktop & mobile UI)
   */
  const SORT_LABELS = {
    recommended: "Recommended",
    newest: "Newest",
    oldest: "Oldest",
    "a-z": "A-Z",
    "z-a": "Z-A"
  };

  /**
   * v1.4.0: Desktop sort label mapping (longer labels for desktop dropdown)
   */
  const SORT_LABELS_DESKTOP = {
    recommended: "Recommended",
    newest: "Newest",
    oldest: "Oldest",
    "a-z": "Alphabetical (A-Z)",
    "z-a": "Alphabetical (Z-A)"
  };

  async function postFavorite(article) {
    try {
      const url = window.ENV_CONFIG?.getProxyUrl('/clients/favorites') || '/apps/clients/favorites';
      const keyname = (article.object_type || article.content_type) == "offering" ? "product_id" : "article_id";

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          [keyname]: article.article_id,
          article_title: article.article_title,
          article_url: article.article_url,
          image_url: article.image_url,
          list_organization: FAVORITES_ORGANIZATION,
          object_type: article.object_type || article.content_type || "article",
        }),
      });

      const data = await response.json();
      return data.status === 0 || data.success === true || !data.error;
    } catch (err) {
      console.error("❌ Error adding favorite:", err);
      return false;
    }
  }

  async function deleteFavorite(articleId) {
    try {
      const url = window.ENV_CONFIG?.getProxyUrl('/clients/favorites') || '/apps/clients/favorites';
      const keyname = articleId.includes("Product") ? "product_id" : "article_id";

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          [keyname]: articleId,
          remove: true,
        }),
      });

      const data = await response.json();
      return data.status === 0 || data.success === true || !data.error;
    } catch (err) {
      console.error("❌ Error removing favorite:", err);
      return false;
    }
  }

  class LifeJourneyFilter {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.data = window.lifeJourneyData?.[sectionId] || {
        categories: [],
        articles: [],
        offerings: [],
        settings: {},
      };

      this.settings = this.data.settings || {};
      this.dataSource = this.settings.dataSource || "embedded";
      this.displayMode = this.settings.displayMode || "slider";
      this.paginationType = this.settings.paginationType || "auto";
      this.titleBehavior = this.settings.titleBehavior || "dynamic";

      this.state = {
        selectedL1: null,
        selectedL2: [],
        selectedTypes: ["article", "offering"],
        currentPage: 1,
        itemsPerPage: this.settings.itemsPerPage || 9,
        allItems: [],
        isLoading: false,
        hasMore: true,
        totalPages: 1,
        totalItemsFromAPI: undefined,
        favorites: [],
        sortBy: "recommended",  // v1.4.0: Changed default from "newest" to "recommended"
      };

      this.dragState = {
        isDragging: false,
        startY: 0,
        currentY: 0,
        menuElement: null,
        wrapperElement: null
      };

      this.productImageCache = {};

      this.categoryMap = this.buildCategoryMap();
      this.init();
    }

    init() {
      const hasURLParams = this.loadStateFromURL();

      if (!hasURLParams) {
        if (this.categoryMap.l1.length > 0) {
          const firstL1 = this.categoryMap.l1[0];
          this.state.selectedL1 = firstL1.id;
          const firstL1Children = this.getL2Children(firstL1.id);
          this.state.selectedL2 = firstL1Children.map((l2) => l2.id);
        }
      }

      this.createMobileOverlay();
      this.renderCategoryFilters();
      this.renderMobileCategoryFilters();
      this.bindEvents();
      this.bindSortingEvents();
      this.bindMobileFilterEvents();
      this.syncDesktopTypeFilters();
      this.syncMobileTypeFilters();
      this.updatePageHeader();
      this.renderResults();

      if (this.state.selectedL2.length > 0) {
        this.updateGradient(this.state.selectedL2[0]);
      } else if (this.state.selectedL1) {
        this.updateGradient(this.state.selectedL1);
      }
      
      this.renderResults();
    }

    buildCategoryMap() {
      // v1.5.0: Added byTitleNormalized for case-insensitive tag matching
      const map = { l1: [], l2: [], byId: {}, byTitle: {}, byTitleNormalized: {} };

      this.data.categories.forEach((cat) => {
        let level = "";
        if (typeof cat.level === "string") {
          level = cat.level.toLowerCase();
        } else if (cat.level?.value) {
          level = cat.level.value.toLowerCase();
        } else if (cat.level?.key) {
          level = cat.level.key.toLowerCase();
        }

        const slug = this.generateSlug(cat.title);

        let parentId = null;
        if (cat.parent) {
          if (typeof cat.parent === "string") {
            parentId = cat.parent;
          } else if (cat.parent.id) {
            parentId = cat.parent.id;
          } else if (cat.parent.value) {
            parentId = cat.parent.value;
          } else if (cat.parent.system?.id) {
            parentId = cat.parent.system.id;
          }
        }

        if (!cat.id) return;

        const category = { id: cat.id, title: cat.title, slug: slug, level: level, parentId: parentId };
        map.byId[cat.id] = category;
        map.byTitle[cat.title] = category;
        // v1.5.0: normalized lookup — lowercase, & → and, collapse non-alphanumeric
        map.byTitleNormalized[this.normalizeTagTitle(cat.title)] = category;

        if (level === "l1") map.l1.push(category);
        else if (level === "l2") map.l2.push(category);
      });

      return map;
    }

    // v1.5.0: Normalize tag/category titles for case & punctuation tolerant matching
    normalizeTagTitle(title) {
      if (!title) return "";
      return title
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    }

    // v1.5.0: Check if an item matches selected categories using both primary
    // category and all tag-mapped categories (multi-category matching)
    itemMatchesSelectedCategories(item) {
      const primaryCatId = item.category?.id || item.category;
      const allCatIds = [primaryCatId].concat(item.categories || []).filter(Boolean);

      const matchesL1 = this.state.selectedL1 && allCatIds.includes(this.state.selectedL1);
      const matchesL2 = this.state.selectedL2.length > 0 &&
        allCatIds.some(function(id) { return this.state.selectedL2.includes(id); }, this);

      return matchesL1 || matchesL2;
    }

    generateSlug(title) {
      if (!title) return "";
      return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    }

    getParentChain(categoryId) {
      const chain = [];
      let current = this.categoryMap.byId[categoryId];
      let iterations = 0;
      const maxIterations = 10;

      while (current && iterations < maxIterations) {
        chain.unshift(current);
        current = current.parentId ? this.categoryMap.byId[current.parentId] : null;
        iterations++;
      }

      return chain;
    }

    getL1Parent(l2CategoryId) {
      const chain = this.getParentChain(l2CategoryId);
      return chain.find((cat) => cat.level === "l1");
    }

    getL2Children(l1CategoryId) {
      return this.categoryMap.l2.filter((l2) => l2.parentId === l1CategoryId);
    }

    getArticlePlaceholderByCategory(categoryId) {
      const category = this.categoryMap.byId[categoryId];
      if (!category) {
        return this.settings.articlePlaceholder || null;
      }

      let l1Category = null;
      
      if (category.level === 'l1') {
        l1Category = category;
      } else if (category.level === 'l2') {
        l1Category = this.getL1Parent(categoryId);
      }

      if (!l1Category) {
        return this.settings.articlePlaceholder || null;
      }

      const l1Title = l1Category.title;
      
      if (this.settings.category1Name && l1Title === this.settings.category1Name) {
        if (this.settings.category1ArticlePlaceholder) {
          return this.settings.category1ArticlePlaceholder;
        }
      }
      
      if (this.settings.category2Name && l1Title === this.settings.category2Name) {
        if (this.settings.category2ArticlePlaceholder) {
          return this.settings.category2ArticlePlaceholder;
        }
      }

      return this.settings.articlePlaceholder || null;
    }

    isValidImageUrl(url) {
      if (!url || typeof url !== 'string') return false;
      const trimmed = url.trim();
      if (trimmed === '' || trimmed === 'null') return false;
      if (trimmed.startsWith('gid://')) return false;
      return true;
    }

    resolveDisplayImage(item, type) {
      if (this.isValidImageUrl(item.preview_image_url)) {
        return item.preview_image_url;
      }

      if (this.isValidImageUrl(item.featured_image)) {
        return item.featured_image;
      }
      if (this.isValidImageUrl(item.image_url)) {
        return item.image_url;
      }

      if (type === "article") {
        const categoryId = item.category?.id || item.category;
        const categoryPlaceholder = this.getArticlePlaceholderByCategory(categoryId);
        if (categoryPlaceholder) {
          return categoryPlaceholder;
        }
        return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="400"%3E%3Crect fill="%23ffffff" width="600" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Montserrat, sans-serif" font-size="24" font-weight="600" fill="%23333"%3EArticle%3C/text%3E%3C/svg%3E';
      }

      if (type === "offering" && this.settings.offeringPlaceholder) {
        return this.settings.offeringPlaceholder;
      }

      return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="400"%3E%3Crect fill="%23e0d6e8" width="600" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Montserrat, sans-serif" font-size="24" font-weight="600" fill="%23333"%3EProduct%3C/text%3E%3C/svg%3E';
    }

    getAuthorLogo(item) {
      if (item.company_logo) return item.company_logo;
      if (item.logo) return item.logo;
      if (item.author_logo) return item.author_logo;
      if (item.author_image) return item.author_image;
      
      if (item.author && typeof item.author === 'object') {
        if (item.author.image) return item.author.image;
        if (item.author.logo) return item.author.logo;
        if (item.author.avatar) return item.author.avatar;
      }
      
      if (this.settings.defaultAuthorLogo) return this.settings.defaultAuthorLogo;
      return null;
    }

    loadStateFromURL() {
      const params = new URLSearchParams(window.location.search);
      let hasParams = false;

      if (params.has("category")) {
        const slug = params.get("category");
        const l1Category = this.categoryMap.l1.find((cat) => cat.slug === slug);
        if (l1Category) {
          this.state.selectedL1 = l1Category.id;
          hasParams = true;
        }
      }

      if (params.has("subcategories")) {
        const slugs = params.get("subcategories").split(",").filter((s) => s);
        slugs.forEach((slug) => {
          const l2Category = this.categoryMap.l2.find((cat) => cat.slug === slug);
          if (l2Category) {
            this.state.selectedL2.push(l2Category.id);
          }
        });
        hasParams = true;
      }

      if (params.has("types")) {
        this.state.selectedTypes = params.get("types").split(",").filter((t) => t);
        hasParams = true;
      }

      return hasParams;
    }

    updateURL() {
      const params = new URLSearchParams();

      if (this.state.selectedL1) {
        const l1Cat = this.categoryMap.byId[this.state.selectedL1];
        if (l1Cat) params.set("category", l1Cat.slug);
      }

      if (this.state.selectedL2.length > 0) {
        const l2Slugs = this.state.selectedL2
          .map((id) => this.categoryMap.byId[id])
          .filter((cat) => cat)
          .map((cat) => cat.slug);
        if (l2Slugs.length > 0) {
          params.set("subcategories", l2Slugs.join(","));
        }
      }

      if (this.state.selectedTypes.length > 0 && this.state.selectedTypes.length < 2) {
        params.set("types", this.state.selectedTypes.join(","));
      }

      const newURL = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
      window.history.pushState({}, "", newURL);
    }

    renderCategoryFilters() {
      const container = document.getElementById("categoryFilters-" + this.sectionId);
      if (!container) return;

      const html = this.categoryMap.l1
        .map((l1, index) => {
          const l2Children = this.getL2Children(l1.id);
          const isExpanded = this.state.selectedL1 === l1.id;

          return '<div class="category-group ' + (isExpanded ? "expanded" : "") + '" data-category-id="' + l1.id + '">' +
            '<button class="category-toggle" data-l1-id="' + l1.id + '" data-index="' + index + '" type="button">' +
            this.sentenceCase(l1.title) +
            '<svg class="chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">' +
            '<path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
            '</button>' +
            '<div class="subcategory-list">' +
            (l2Children.length > 0
              ? l2Children.map((l2) =>
                  '<label class="filter-checkbox">' +
                  '<input type="checkbox" name="l2category" value="' + l2.id + '" data-l1-parent="' + l1.id + '"' +
                  (this.state.selectedL2.includes(l2.id) ? " checked" : "") + '>' +
                  '<span>' + this.sentenceCase(l2.title) + '</span>' +
                  '</label>'
                ).join("")
              : '<p style="padding: 8px 0; font-size: 13px; color: #999;">No subcategories</p>') +
            '</div>' +
            '</div>';
        })
        .join("");

      container.innerHTML = html;
    }

    createMobileOverlay() {}

    renderMobileCategoryFilters() {
      const container = document.querySelector('#mobileCategoryDropdown-' + this.sectionId + ' .mobile-dropdown-content');
      if (!container) return;

      const html = this.categoryMap.l1
        .map((l1) => {
          const l2Children = this.getL2Children(l1.id);
          const isExpanded = this.state.selectedL1 === l1.id;

          return '<div class="mobile-category-group ' + (isExpanded ? "expanded" : "") + '" data-l1-id="' + l1.id + '">' +
            '<button class="mobile-category-header" type="button" data-l1-id="' + l1.id + '">' +
            l1.title +
            '<svg width="10" height="6" viewBox="0 0 10 6" fill="none">' +
            '<path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
            '</button>' +
            '<div class="mobile-subcategory-list">' +
            l2Children.map((l2) =>
              '<label class="mobile-subcategory-checkbox">' +
              '<input type="checkbox" name="mobile-l2category" value="' + l2.id + '" data-l1-parent="' + l1.id + '"' +
              (this.state.selectedL2.includes(l2.id) ? " checked" : "") + '>' +
              '<span>' + l2.title + '</span>' +
              '</label>'
            ).join("") +
            '</div>' +
            '</div>';
        })
        .join("");

      container.innerHTML = html;
      this.updateMobileCategoryButtonText();
    }

    updateMobileCategoryButtonText() {
      const btn = document.querySelector('#mobileCategoryDropdown-' + this.sectionId + ' .mobile-dropdown-text');
      if (!btn) return;

      if (this.state.selectedL1) {
        const l1Cat = this.categoryMap.byId[this.state.selectedL1];
        const l2Children = this.getL2Children(this.state.selectedL1);
        const selectedCount = this.state.selectedL2.filter(id => l2Children.some(l2 => l2.id === id)).length;
        
        if (selectedCount === l2Children.length) {
          btn.textContent = l1Cat?.title || "Category";
        } else if (selectedCount > 0) {
          btn.textContent = l1Cat?.title + " (" + selectedCount + ")";
        } else {
          btn.textContent = l1Cat?.title || "Category";
        }
      } else {
        btn.textContent = "Category";
      }
    }

    updateMobileTypeButtonText() {
      const btn = document.querySelector('#mobileTypeDropdown-' + this.sectionId + ' .mobile-dropdown-text');
      if (!btn) return;

      const types = this.state.selectedTypes;
      if (types.length === 2) {
        btn.textContent = "All";
      } else if (types.length === 0) {
        btn.textContent = "None";
      } else if (types.includes("article")) {
        btn.textContent = "Articles";
      } else if (types.includes("offering")) {
        btn.textContent = "Offerings";
      }
    }

    updateMobileSortButtonText() {
      const btn = document.querySelector('#mobileSortDropdown-' + this.sectionId + ' .mobile-dropdown-text');
      if (!btn) return;

      btn.textContent = SORT_LABELS[this.state.sortBy] || "Recommended";
    }

    openMobileDropdown(wrapper, menu) {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      
      section.querySelectorAll('.mobile-dropdown-wrapper').forEach(w => {
        w.classList.remove('open');
        const m = w.querySelector('.mobile-dropdown-menu');
        if (m) {
          m.style.display = 'none';
          m.style.transform = 'translateY(100%)';
        }
      });

      wrapper.classList.add('open');
      menu.style.display = 'block';
      menu.offsetHeight;
      menu.style.transform = 'translateY(0)';
      document.body.style.overflow = 'hidden';
    }

    closeMobileDropdowns() {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;
      
      section.querySelectorAll('.mobile-dropdown-wrapper').forEach(w => {
        const m = w.querySelector('.mobile-dropdown-menu');
        if (m && w.classList.contains('open')) {
          m.style.transform = 'translateY(100%)';
          setTimeout(() => {
            w.classList.remove('open');
            m.style.display = 'none';
          }, 300);
        }
      });

      document.body.style.overflow = '';
    }

    handleDragStart(e, menu, wrapper) {
      const touch = e.touches ? e.touches[0] : e;
      this.dragState = {
        isDragging: true,
        startY: touch.clientY,
        currentY: touch.clientY,
        menuElement: menu,
        wrapperElement: wrapper,
        menuHeight: menu.offsetHeight
      };
      menu.style.transition = 'none';
    }

    handleDragMove(e) {
      if (!this.dragState.isDragging) return;
      const touch = e.touches ? e.touches[0] : e;
      this.dragState.currentY = touch.clientY;
      const deltaY = this.dragState.currentY - this.dragState.startY;
      if (deltaY > 0) {
        this.dragState.menuElement.style.transform = 'translateY(' + deltaY + 'px)';
      }
    }

    handleDragEnd() {
      if (!this.dragState.isDragging) return;
      const deltaY = this.dragState.currentY - this.dragState.startY;
      const threshold = this.dragState.menuHeight * 0.3;
      this.dragState.menuElement.style.transition = 'transform 0.3s ease';
      if (deltaY > threshold) {
        this.closeMobileDropdowns();
      } else {
        this.dragState.menuElement.style.transform = 'translateY(0)';
      }
      this.dragState.isDragging = false;
    }

    bindMobileFilterEvents() {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;

      const self = this;

      const categoryBtn = section.querySelector('#mobileCategoryDropdown-' + this.sectionId + ' .mobile-dropdown-btn');
      const categoryMenu = section.querySelector('#mobileCategoryDropdown-' + this.sectionId + ' .mobile-dropdown-menu');

      if (categoryBtn && categoryMenu) {
        categoryBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          const wrapper = categoryBtn.closest('.mobile-dropdown-wrapper');
          if (wrapper.classList.contains('open')) {
            self.closeMobileDropdowns();
          } else {
            self.openMobileDropdown(wrapper, categoryMenu);
          }
        });
      }

      const typeBtn = section.querySelector('#mobileTypeDropdown-' + this.sectionId + ' .mobile-dropdown-btn');
      const typeMenu = section.querySelector('#mobileTypeDropdown-' + this.sectionId + ' .mobile-dropdown-menu');

      if (typeBtn && typeMenu) {
        typeBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          const wrapper = typeBtn.closest('.mobile-dropdown-wrapper');
          if (wrapper.classList.contains('open')) {
            self.closeMobileDropdowns();
          } else {
            self.openMobileDropdown(wrapper, typeMenu);
          }
        });
      }

      const sortBtn = section.querySelector('#mobileSortDropdown-' + this.sectionId + ' .mobile-dropdown-btn');
      const sortMenu = section.querySelector('#mobileSortDropdown-' + this.sectionId + ' .mobile-dropdown-menu');

      if (sortBtn && sortMenu) {
        sortBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          const wrapper = sortBtn.closest('.mobile-dropdown-wrapper');
          if (wrapper.classList.contains('open')) {
            self.closeMobileDropdowns();
          } else {
            self.openMobileDropdown(wrapper, sortMenu);
          }
        });
      }

      section.querySelectorAll('.mobile-dropdown-done').forEach(function(doneBtn) {
        doneBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          self.closeMobileDropdowns();
        });
      });

      section.querySelectorAll('.mobile-dropdown-handle').forEach(function(handle) {
        handle.addEventListener('click', function(e) {
          if (!self.dragState.isDragging) {
            self.closeMobileDropdowns();
          }
        });
      });

      section.querySelectorAll('.mobile-dropdown-menu').forEach(function(menu) {
        const wrapper = menu.closest('.mobile-dropdown-wrapper');
        const handle = menu.querySelector('.mobile-dropdown-handle');
        
        if (handle) {
          handle.addEventListener('touchstart', function(e) {
            self.handleDragStart(e, menu, wrapper);
          }, { passive: true });
          
          handle.addEventListener('mousedown', function(e) {
            self.handleDragStart(e, menu, wrapper);
          });
        }
      });

      document.addEventListener('touchmove', function(e) {
        if (self.dragState.isDragging) {
          self.handleDragMove(e);
        }
      }, { passive: true });

      document.addEventListener('touchend', function() {
        if (self.dragState.isDragging) {
          self.handleDragEnd();
        }
      });

      document.addEventListener('mousemove', function(e) {
        if (self.dragState.isDragging) {
          self.handleDragMove(e);
        }
      });

      document.addEventListener('mouseup', function() {
        if (self.dragState.isDragging) {
          self.handleDragEnd();
        }
      });

      section.addEventListener('click', function(e) {
        const categoryHeader = e.target.closest('.mobile-category-header');
        if (categoryHeader) {
          e.stopPropagation();
          const group = categoryHeader.closest('.mobile-category-group');
          const l1Id = categoryHeader.dataset.l1Id;

          if (self.state.selectedL1 === l1Id) {
            return;
          }

          section.querySelectorAll('.mobile-category-group').forEach(function(g) {
            if (g !== group) g.classList.remove('expanded');
          });

          group.classList.add('expanded');
          self.state.selectedL1 = l1Id;
          const l2Children = self.getL2Children(l1Id);
          self.state.selectedL2 = l2Children.map(function(l2) { return l2.id; });
          
          section.querySelectorAll('input[name="mobile-l2category"]').forEach(function(cb) {
            cb.checked = self.state.selectedL2.includes(cb.value);
          });

          self.syncDesktopFilters();
          self.updateMobileCategoryButtonText();
          self.resetPagination();
          self.updateURL();
          self.updatePageHeader();
          self.updateGradient(l1Id);
          self.renderResults();
          return;
        }

        const sortOption = e.target.closest('.mobile-dropdown-option[data-sort]');
        if (sortOption) {
          e.stopPropagation();
          const sortValue = sortOption.dataset.sort;
          self.state.sortBy = sortValue;

          section.querySelectorAll('#mobileSortDropdown-' + self.sectionId + ' .mobile-dropdown-option').forEach(function(opt) {
            opt.classList.remove('active');
          });
          sortOption.classList.add('active');

          self.closeMobileDropdowns();
          self.updateMobileSortButtonText();
          self.syncDesktopSort();
          self.resetPagination();
          self.renderResults();
          return;
        }
      });

      section.addEventListener('change', function(e) {
        if (e.target.matches('input[name="mobile-l2category"]')) {
          const checkbox = e.target;
          const l2Id = checkbox.value;
          const l1ParentId = checkbox.dataset.l1Parent;

          if (checkbox.checked) {
            if (!self.state.selectedL2.includes(l2Id)) {
              self.state.selectedL2.push(l2Id);
            }
            if (self.state.selectedL1 !== l1ParentId) {
              self.state.selectedL1 = l1ParentId;
            }
            self.updateGradient(l2Id);
          } else {
            self.state.selectedL2 = self.state.selectedL2.filter(function(id) {
              return id !== l2Id;
            });

            const l2Children = self.getL2Children(l1ParentId);
            const hasSelectedL2 = l2Children.some(function(l2) {
              return self.state.selectedL2.includes(l2.id);
            });

            if (!hasSelectedL2) {
              self.state.selectedL1 = null;
              self.updateGradient(null);
            } else {
              self.updateGradient(self.state.selectedL2[0]);
            }
          }

          self.syncDesktopFilters();
          self.updateMobileCategoryButtonText();
          self.resetPagination();
          self.updateURL();
          self.updatePageHeader();
          self.renderResults();
        }

        if (e.target.matches('input[name="mobile-type"]')) {
          const checkbox = e.target;
          const type = checkbox.value;

          if (checkbox.checked) {
            if (!self.state.selectedTypes.includes(type)) {
              self.state.selectedTypes.push(type);
            }
          } else {
            self.state.selectedTypes = self.state.selectedTypes.filter(function(t) {
              return t !== type;
            });
          }

          self.syncDesktopTypeFilters();
          self.updateMobileTypeButtonText();
          self.resetPagination();
          self.updateURL();
          self.renderResults();
        }
      });
    }

    syncDesktopFilters() {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;

      const self = this;

      section.querySelectorAll('.category-group').forEach(function(group) {
        const groupId = group.dataset.categoryId;
        if (groupId === self.state.selectedL1) {
          group.classList.add('expanded');
        } else {
          group.classList.remove('expanded');
        }
      });

      section.querySelectorAll('input[name="l2category"]').forEach(function(cb) {
        cb.checked = self.state.selectedL2.includes(cb.value);
      });
    }

    syncDesktopTypeFilters() {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;

      const self = this;

      section.querySelectorAll('input[name="type"]').forEach(function(cb) {
        cb.checked = self.state.selectedTypes.includes(cb.value);
      });
    }

    syncMobileTypeFilters() {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;

      const self = this;

      section.querySelectorAll('input[name="mobile-type"]').forEach(function(cb) {
        cb.checked = self.state.selectedTypes.includes(cb.value);
      });
      
      this.updateMobileTypeButtonText();
    }

    syncDesktopSort() {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;

      const self = this;

      const currentSortText = section.querySelector('.current-sort');
      if (currentSortText) {
        currentSortText.textContent = SORT_LABELS_DESKTOP[self.state.sortBy] || "Recommended";
      }

      section.querySelectorAll('.sort-option').forEach(function(opt) {
        opt.classList.toggle('active', opt.dataset.sort === self.state.sortBy);
      });
    }

    syncMobileFilters() {
      this.renderMobileCategoryFilters();
      this.updateMobileTypeButtonText();
      this.updateMobileSortButtonText();

      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;

      const self = this;

      section.querySelectorAll('input[name="mobile-type"]').forEach(function(cb) {
        cb.checked = self.state.selectedTypes.includes(cb.value);
      });
    }

    bindSortingEvents() {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;

      const sortButton = section.querySelector('.sort-button');
      const sortDropdown = section.querySelector('.sort-dropdown');
      const sortOptions = section.querySelectorAll('.sort-option');

      if (!sortButton || !sortDropdown) return;

      const self = this;

      sortButton.addEventListener('click', function(e) {
        e.stopPropagation();
        sortDropdown.style.display = sortDropdown.style.display === 'none' ? 'block' : 'none';
      });

      document.addEventListener('click', function() {
        sortDropdown.style.display = 'none';
      });

      sortOptions.forEach(function(option) {
        option.addEventListener('click', function(e) {
          e.stopPropagation();
          const sortValue = option.dataset.sort;

          self.state.sortBy = sortValue;

          sortOptions.forEach(function(opt) {
            opt.classList.remove('active');
          });
          option.classList.add('active');

          const currentSortText = sortButton.querySelector('.current-sort');
          currentSortText.textContent = option.textContent;

          sortDropdown.style.display = 'none';

          self.updateMobileSortButtonText();
          section.querySelectorAll('#mobileSortDropdown-' + self.sectionId + ' .mobile-dropdown-option').forEach(function(opt) {
            opt.classList.toggle('active', opt.dataset.sort === sortValue);
          });

          self.resetPagination();
          self.renderResults();
        });
      });
    }

    /**
     * v1.4.0: Apply client-side sorting.
     * "recommended" returns array as-is (preserves API/embedded order).
     */
    applySorting(items) {
      if (this.state.sortBy === "recommended") {
        return items.slice();
      }

      const sorted = items.slice();

      switch (this.state.sortBy) {
        case "newest":
          sorted.sort(function(a, b) {
            const dateA = new Date(a.created_at || a.published_at || 0);
            const dateB = new Date(b.created_at || b.published_at || 0);
            return dateB - dateA;
          });
          break;
        case "oldest":
          sorted.sort(function(a, b) {
            const dateA = new Date(a.created_at || a.published_at || 0);
            const dateB = new Date(b.created_at || b.published_at || 0);
            return dateA - dateB;
          });
          break;
        case "a-z":
          sorted.sort(function(a, b) {
            const titleA = (a.title || "").toLowerCase();
            const titleB = (b.title || "").toLowerCase();
            return titleA.localeCompare(titleB);
          });
          break;
        case "z-a":
          sorted.sort(function(a, b) {
            const titleA = (a.title || "").toLowerCase();
            const titleB = (b.title || "").toLowerCase();
            return titleB.localeCompare(titleA);
          });
          break;
      }

      return sorted;
    }

    bindEvents() {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;

      const self = this;

      section.addEventListener("click", function(e) {
        const favoriteBtn = e.target.closest(".favorite-btn");
        if (favoriteBtn) {
          e.preventDefault();
          e.stopPropagation();
          self.handleFavoriteClick(favoriteBtn);
          return;
        }

        const toggle = e.target.closest(".category-toggle");
        if (toggle) {
          e.preventDefault();
          const l1Id = toggle.getAttribute("data-l1-id");
          self.toggleL1Category(l1Id);
          return;
        }

        const loadMoreBtn = e.target.closest(".load-more-btn");
        if (loadMoreBtn && !loadMoreBtn.disabled) {
          self.loadMore();
          return;
        }

        const paginationBtn = e.target.closest(".pagination-btn");
        if (paginationBtn && !paginationBtn.disabled) {
          const raw = paginationBtn.getAttribute("data-page");
          const page = parseInt(raw, 10);
          if (page && !isNaN(page)) {
            self.goToPage(page);
          }
        }
      });

      section.addEventListener("change", function(e) {
        if (e.target.matches('input[name="l2category"]')) {
          self.handleL2Change(e.target);
        }
      });

      section.addEventListener("change", function(e) {
        if (e.target.matches('input[name="type"]')) {
          self.handleTypeChange(e.target);
        }
      });

      if (this.displayMode === "grid") {
        this.setupInfiniteScroll();
      }
    }

    setupInfiniteScroll() {
      if (this.paginationType !== "auto") return;

      const self = this;
      let isScrolling;

      window.addEventListener("scroll", function() {
        if (self.state.isLoading || !self.state.hasMore) return;

        clearTimeout(isScrolling);

        isScrolling = setTimeout(function() {
          const resultsContainer = document.getElementById("resultsContainer-" + self.sectionId);
          if (!resultsContainer) return;

          const rect = resultsContainer.getBoundingClientRect();
          const isNearBottom = rect.bottom <= window.innerHeight + 500;

          if (isNearBottom) {
            self.loadMore();
          }
        }, 100);
      });
    }

    async handleFavoriteClick(button) {
      const id = button.dataset.id;
      const title = button.dataset.title || "";
      const url = button.dataset.url || "#";
      const image = button.dataset.image || "";
      const type = button.dataset.type || "article";

      if (!id) {
        console.warn("Favorite button missing data-id");
        return;
      }

      const isActive = button.classList.contains("is-favorited");
      const payload = {
        article_id: id,
        article_title: title,
        article_url: url,
        image_url: image,
        list_organization: FAVORITES_ORGANIZATION,
        object_type: type,
      };

      try {
        if (isActive) {
          const ok = await deleteFavorite(id);
          if (ok) {
            button.classList.remove("is-favorited");
            button.setAttribute("aria-pressed", "false");
            button.setAttribute("aria-label", "Add to favorites");
            this.state.favorites = this.state.favorites.filter(function(favId) {
              return favId !== id;
            });
            const svg = button.querySelector("svg");
            if (svg) svg.setAttribute("fill", "none");
          }
        } else {
          const ok = await postFavorite(payload);
          if (ok) {
            button.classList.add("is-favorited");
            button.setAttribute("aria-pressed", "true");
            button.setAttribute("aria-label", "Remove from favorites");
            if (!this.state.favorites.includes(id)) {
              this.state.favorites.push(id);
            }
            const svg = button.querySelector("svg");
            if (svg) svg.setAttribute("fill", "currentColor");
          }
        }
      } catch (error) {
        console.error("Error toggling favorite:", error);
      }
    }

    toggleL1Category(l1Id) {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      const wasExpanded = this.state.selectedL1 === l1Id;

      if (wasExpanded) {
        return;
      }

      const self = this;

      section.querySelectorAll(".category-group").forEach(function(group) {
        group.classList.remove("expanded");
      });

      this.state.selectedL1 = l1Id;
      const group = section.querySelector('[data-category-id="' + l1Id + '"]');
      if (group) {
        group.classList.add("expanded");
      }

      const l2Children = this.getL2Children(l1Id);
      this.state.selectedL2 = l2Children.map(function(l2) {
        return l2.id;
      });

      section.querySelectorAll('input[name="l2category"]').forEach(function(cb) {
        const l2Id = cb.value;
        cb.checked = self.state.selectedL2.includes(l2Id);
      });

      this.updateGradient(l1Id);
      this.syncMobileFilters();
      this.resetPagination();
      this.updateURL();
      this.updatePageHeader();
      this.renderResults();
    }

    handleL2Change(checkbox) {
      const l2Id = checkbox.value;
      const l1ParentId = checkbox.dataset.l1Parent;

      if (checkbox.checked) {
        if (!this.state.selectedL2.includes(l2Id)) {
          this.state.selectedL2.push(l2Id);
        }
        if (this.state.selectedL1 !== l1ParentId) {
          this.state.selectedL1 = l1ParentId;
        }
        this.updateGradient(l2Id);
      } else {
        this.state.selectedL2 = this.state.selectedL2.filter(function(id) {
          return id !== l2Id;
        });

        if (this.state.selectedL2.length === 0) {
          this.state.selectedL1 = null;
          this.updateGradient(null);
        } else {
          this.updateGradient(this.state.selectedL2[0]);
        }
      }

      this.syncMobileFilters();
      this.resetPagination();
      this.updateURL();
      this.updatePageHeader();
      this.renderResults();
    }

    handleTypeChange(checkbox) {
      const type = checkbox.value;

      if (checkbox.checked) {
        if (!this.state.selectedTypes.includes(type)) {
          this.state.selectedTypes.push(type);
        }
      } else {
        this.state.selectedTypes = this.state.selectedTypes.filter(function(t) {
          return t !== type;
        });
      }

      this.syncMobileFilters();
      this.resetPagination();
      this.updateURL();
      this.renderResults();
    }

    resetPagination() {
      this.state.currentPage = 1;
      this.state.allItems = [];
      this.state.hasMore = true;
      this.state.totalItemsFromAPI = undefined;
    }

    sentenceCase(str) {
      if (!str) return str;
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    updatePageHeader() {
      const titleEl = document.getElementById("pageTitle-" + this.sectionId);
      const descEl = document.getElementById("pageDescription-" + this.sectionId);

      if (!titleEl || !descEl) return;

      const breadcrumbType = this.settings.breadcrumbType || "categories";

      if (breadcrumbType === "categories") {
        this.updateCategoriesBreadcrumb();
      }

      const categoryDescriptions = window.categoryDescriptions || {};
      const defaultDescription = this.settings.defaultDescription || "";

      if (this.titleBehavior === "static") {
        if (this.state.selectedL1) {
          const l1Cat = this.categoryMap.byId[this.state.selectedL1];
          titleEl.textContent = this.sentenceCase(l1Cat?.title || "");
          descEl.innerHTML = this.sentenceCase(categoryDescriptions[l1Cat?.title] || defaultDescription);
        } else {
          titleEl.textContent = this.settings.defaultTitle || "Journey";
          descEl.innerHTML = defaultDescription;
        }
        return;
      }

      var allL2OfCurrentL1Selected = false;
      if (this.state.selectedL1) {
        const l2Children = this.getL2Children(this.state.selectedL1);
        const l2ChildrenIds = l2Children.map(function(l2) { return l2.id; });
        const self = this;
        allL2OfCurrentL1Selected =
          l2ChildrenIds.length > 0 &&
          l2ChildrenIds.every(function(id) { return self.state.selectedL2.includes(id); }) &&
          this.state.selectedL2.length === l2ChildrenIds.length;
      }

      if (this.state.selectedL1 && allL2OfCurrentL1Selected) {
        const l1Cat = this.categoryMap.byId[this.state.selectedL1];
        titleEl.textContent = l1Cat?.title || "";
        descEl.innerHTML = categoryDescriptions[l1Cat?.title] || defaultDescription;
        return;
      }

      if (this.state.selectedL2.length > 0 && !allL2OfCurrentL1Selected) {
        const self = this;
        const l2Categories = this.state.selectedL2
          .map(function(id) { return self.categoryMap.byId[id]; })
          .filter(function(cat) { return cat; });

        const l2Titles = l2Categories.map(function(cat) { return cat.title; });

        titleEl.textContent = l2Titles.join(" | ");
        if (this.state.selectedL1) {
          const l1Cat = this.categoryMap.byId[this.state.selectedL1];
          descEl.innerHTML = categoryDescriptions[l1Cat?.title] || defaultDescription;
        } else {
          descEl.innerHTML = defaultDescription;
        }
      } else if (this.state.selectedL1) {
        const l1Cat = this.categoryMap.byId[this.state.selectedL1];
        titleEl.textContent = l1Cat?.title || "";
        descEl.innerHTML = categoryDescriptions[l1Cat?.title] || defaultDescription;
      } else {
        titleEl.textContent = this.settings.defaultTitle || "Life Journey";
        descEl.innerHTML = defaultDescription;
      }
    }

    updateCategoriesBreadcrumb() {
      const breadcrumbNav = document.getElementById("breadcrumbNav-" + this.sectionId);
      if (!breadcrumbNav) return;

      const homeText = this.settings.breadcrumbHomeText || "Life Journey";
      const homeUrl = this.settings.breadcrumbHomeUrl || "/pages/life-journey";

      if (this.titleBehavior === "static") {
        if (this.state.selectedL1) {
          const l1Cat = this.categoryMap.byId[this.state.selectedL1];
          breadcrumbNav.innerHTML =
            '<a href="' + homeUrl + '">' + homeText + '</a>' +
            '<span class="breadcrumb-separator">›</span>' +
            '<span class="breadcrumb-current">' + this.sentenceCase(l1Cat?.title || "") + '</span>';
        } else {
          breadcrumbNav.innerHTML =
            '<a href="' + homeUrl + '">' + homeText + '</a>' +
            '<span class="breadcrumb-separator">›</span>' +
            '<span class="breadcrumb-current">All categories</span>';
        }
        return;
      }

      var allL2OfCurrentL1Selected = false;
      if (this.state.selectedL1) {
        const l2Children = this.getL2Children(this.state.selectedL1);
        const l2ChildrenIds = l2Children.map(function(l2) { return l2.id; });
        const self = this;
        allL2OfCurrentL1Selected =
          l2ChildrenIds.length > 0 &&
          l2ChildrenIds.every(function(id) { return self.state.selectedL2.includes(id); }) &&
          this.state.selectedL2.length === l2ChildrenIds.length;
      }

      if (this.state.selectedL1 && allL2OfCurrentL1Selected) {
        const l1Cat = this.categoryMap.byId[this.state.selectedL1];
        breadcrumbNav.innerHTML =
          '<a href="' + homeUrl + '">' + homeText + '</a>' +
          '<span class="breadcrumb-separator">›</span>' +
          '<span class="breadcrumb-current">' + (l1Cat?.title || "") + '</span>';
      } else if (this.state.selectedL2.length > 0 && !allL2OfCurrentL1Selected) {
        const self = this;
        const l2Categories = this.state.selectedL2
          .map(function(id) { return self.categoryMap.byId[id]; })
          .filter(function(cat) { return cat; });

        const l2Titles = l2Categories.map(function(cat) { return cat.title; });

        if (this.state.selectedL1) {
          const l1Cat = this.categoryMap.byId[this.state.selectedL1];
          breadcrumbNav.innerHTML =
            '<a href="' + homeUrl + '">' + homeText + '</a>' +
            '<span class="breadcrumb-separator">›</span>' +
            '<span>' + (l1Cat?.title || "") + '</span>' +
            '<span class="breadcrumb-separator">›</span>' +
            '<span class="breadcrumb-current">' + l2Titles.join(" | ") + '</span>';
        } else {
          breadcrumbNav.innerHTML =
            '<a href="' + homeUrl + '">' + homeText + '</a>' +
            '<span class="breadcrumb-separator">›</span>' +
            '<span class="breadcrumb-current">' + l2Titles.join(" | ") + '</span>';
        }
      } else if (this.state.selectedL1) {
        const l1Cat = this.categoryMap.byId[this.state.selectedL1];
        breadcrumbNav.innerHTML =
          '<a href="' + homeUrl + '">' + homeText + '</a>' +
          '<span class="breadcrumb-separator">›</span>' +
          '<span class="breadcrumb-current">' + (l1Cat?.title || "") + '</span>';
      } else {
        breadcrumbNav.innerHTML =
          '<a href="' + homeUrl + '">' + homeText + '</a>' +
          '<span class="breadcrumb-separator">›</span>' +
          '<span class="breadcrumb-current">All Categories</span>';
      }
    }

    async renderResults() {
      const container = document.getElementById("resultsContainer-" + this.sectionId);
      if (!container) return;

      if (this.state.currentPage === 1 && this.dataSource === "api") {
        this.showLoadingSkeleton(container);
      }

      var results;

      if (this.dataSource === "api") {
        try {
          results = await this.fetchFromAPI();
          
          if (results.offerings && results.offerings.length > 0) {
            await this.fetchMissingProductImages(results.offerings);
          }
          
        } catch (error) {
          console.error("API fetch failed, falling back to embedded data:", error);
          results = this.getFilteredContent();
        }
      } else {
        results = this.getFilteredContent();
      }

      if (this.displayMode === "grid") {
        this.displayGridResults(results);
      } else {
        this.displaySliderResults(results);
      }
    }

    showLoadingSkeleton(container) {
      container.innerHTML = `
        <div class="loading-state">
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="17" fill="none" stroke="#f3f3f3" stroke-width="3"/>
            <path d="M20 3 a17 17 0 0 1 17 17" fill="none" stroke="#6b21a8" stroke-width="3" stroke-linecap="round">
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 20 20"
                to="360 20 20"
                dur="0.8s"
                repeatCount="indefinite"/>
            </path>
          </svg>
          <p style="margin-top: 16px; color: #666; font-family: var(--font-body);">Loading resources...</p>
        </div>
      `;
    }

    getFilteredContent() {
      var articles = [];
      var offerings = [];
      const self = this;

      if (this.displayMode === "grid") {
        if (this.state.selectedL2.length === 0 && this.state.selectedL1 === null) {
          articles = this.data.articles.slice();
          offerings = this.data.offerings.slice();
        } else {
          // v1.5.0: Use multi-category matcher instead of single-category check
          articles = this.data.articles.filter(function(item) {
            return self.itemMatchesSelectedCategories(item);
          });
          offerings = this.data.offerings.filter(function(item) {
            return self.itemMatchesSelectedCategories(item);
          });
        }
      } else {
        // v1.5.0: Use multi-category matcher instead of single-category check
        articles = this.data.articles.filter(function(item) {
          return self.itemMatchesSelectedCategories(item);
        });
        offerings = this.data.offerings.filter(function(item) {
          return self.itemMatchesSelectedCategories(item);
        });
      }

      if (!this.state.selectedTypes.includes("article")) {
        articles = [];
      }
      if (!this.state.selectedTypes.includes("offering")) {
        offerings = [];
      }

      return { articles: articles, offerings: offerings };
    }

    /**
     * v1.4.0: Dynamic sort_field/sort_direction based on user's sort selection
     */
    async fetchFromAPI() {
      const endpointPath = this.settings.apiEndpoint || this.settings.api_endpoint || '/clients/filter';
      const apiEndpoint = window.ENV_CONFIG?.getProxyUrl(endpointPath) || endpointPath;
      const itemsPerCategory = this.settings.itemsPerCategory || this.settings.items_per_category || 10;

      var contentTypes = [];
      if (this.state.selectedTypes.includes("article")) contentTypes.push("articles");
      if (this.state.selectedTypes.includes("offering")) contentTypes.push("products");

      var categoryIds = [];
      if (this.state.selectedL2.length > 0) {
        categoryIds = this.state.selectedL2.map(function(id) {
          return id.startsWith("gid://") ? id : "gid://shopify/Metaobject/" + id;
        });
      }

      var fetchLimit, fetchOffset;

      if (this.displayMode === "grid") {
        fetchLimit = this.state.itemsPerPage;
        fetchOffset = (this.state.currentPage - 1) * this.state.itemsPerPage;
      } else {
        fetchLimit = itemsPerCategory * Math.max(this.state.selectedL2.length, 1);
        fetchOffset = 0;
      }

      // v1.4.0: Map sort selection to API parameters
      const sortParams = SORT_API_MAP[this.state.sortBy] || SORT_API_MAP.recommended;

      const body = {
        category_ids: categoryIds.length > 0 ? categoryIds : undefined,
        content_type: contentTypes,
        sort_direction: sortParams.sort_direction,
        offset: fetchOffset,
        limit: fetchLimit,
      };

      // v1.4.0: Only include sort_field when it's not null (recommended omits it)
      if (sortParams.sort_field !== null) {
        body.sort_field = sortParams.sort_field;
      }

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error("API returned " + response.status);
      }

      const data = await response.json();

      if (data.status !== 0) {
        throw new Error("API returned error status");
      }

      if (data.response.total_count !== undefined) {
        this.state.totalItemsFromAPI = data.response.total_count;
      }

      return this.transformAPIResponse(data.response);
    }

    transformAPIResponse(apiResponse) {
      var articles = [];
      var offerings = [];
      const self = this;

      const profiles = window.participantsProfiles || {};

      const normalizedProfiles = {};
      Object.keys(profiles).forEach(function(key) {
        const normalizedKey = key.toLowerCase().replace(/[''"]/g, '').replace(/\s+/g, ' ').trim();
        normalizedProfiles[normalizedKey] = profiles[key];
      });

      apiResponse.results.forEach(function(item) {
        const categoryId = item.category.replace("gid://shopify/Metaobject/", "");

        // The filter API returns absolute .myshopify.com URLs. Strip that origin
        // so the card link (and the product-image .json fetch) stays relative and
        // follows the shop's custom domain. No-op on relative/external links.
        const itemUrl = self.stripShopHost(item.url);

        // v1.5.0: Map tags array to category IDs using normalized title lookup
        const tagCategoryIds = [];
        if (Array.isArray(item.tags)) {
          item.tags.forEach(function(tagTitle) {
            // Try exact match first, then normalized fallback
            var cat = self.categoryMap.byTitle[tagTitle];
            if (!cat) {
              cat = self.categoryMap.byTitleNormalized[self.normalizeTagTitle(tagTitle)];
            }
            if (cat && !tagCategoryIds.includes(cat.id)) {
              tagCategoryIds.push(cat.id);
            }
          });
        }

        if (item.content_type === "article") {
          const authorName = item.author || "";
          const normalizedAuthor = authorName.toLowerCase().replace(/[''"]/g, '').replace(/\s+/g, ' ').trim();
          
          var profile = profiles[authorName];
          if (!profile) {
            profile = normalizedProfiles[normalizedAuthor];
          }
          if (!profile && normalizedAuthor.endsWith('s')) {
            const singularVersion = normalizedAuthor.slice(0, -1);
            profile = normalizedProfiles[singularVersion];
          }
          if (!profile && !normalizedAuthor.endsWith('s')) {
            const pluralVersion = normalizedAuthor + 's';
            profile = normalizedProfiles[pluralVersion];
          }
          
          const companyLogo = profile ? profile.company_logo : null;

          articles.push({
            id: item.id,
            title: item.title,
            url: itemUrl,
            preview_image_url: item.preview_image_url || null,
            featured_image: item.image_url,
            image_url: item.image_url,
            description: item.summary || "",
            company_name: typeof item.author === 'object' && item.author.name ? item.author.name : item.author || "Author",
            company_logo: companyLogo,
            read_time: 5,
            category: categoryId,
            categories: tagCategoryIds,  // v1.5.0: all tag-mapped category IDs
            content_type: "article",
            published_at: item.published_at,
            created_at: item.created_at,
            updated_at: item.updated_at,
            is_favorite: item.is_favorite,
          });
        } else if (item.content_type === "product") {
          const needsImageFetch = !item.image_url;
          const cachedImage = self.productImageCache[itemUrl];
          
          offerings.push({
            id: item.id,
            title: item.title,
            url: itemUrl,
            preview_image_url: item.preview_image_url || null,
            featured_image: cachedImage || item.image_url,
            image_url: cachedImage || item.image_url,
            description: self.stripHTML(item.description_html || ""),
            price: item.price,
            category: categoryId,
            categories: tagCategoryIds,  // v1.5.0: all tag-mapped category IDs
            content_type: "offering",
            created_at: item.created_at,
            updated_at: item.updated_at,
            is_favorite: item.is_favorite,
            needsImageFetch: needsImageFetch && !cachedImage,
          });
        }
      });

      // v1.4.0: Build combined array preserving original API order
      var combinedMap = {};
      articles.forEach(function(a) { combinedMap[a.id] = a; });
      offerings.forEach(function(o) { combinedMap[o.id] = o; });

      var combined = apiResponse.results.map(function(item) {
        return combinedMap[item.id];
      }).filter(function(item) { return item; });

      return { articles: articles, offerings: offerings, combined: combined };
    }

    async fetchProductImage(productUrl) {
      try {
        if (this.productImageCache[productUrl]) {
          return this.productImageCache[productUrl];
        }

        let cleanUrl = productUrl.split('?')[0];
        if (!cleanUrl.endsWith('.json')) {
          cleanUrl += '.json';
        }

        const response = await fetch(cleanUrl);
        if (!response.ok) return null;

        const data = await response.json();
        const product = data.product;

        if (product) {
          const imageUrl = product.featured_image ||
                          (product.image && product.image.src) ||
                          (product.images && product.images.length > 0 && product.images[0].src);

          if (imageUrl) {
            this.productImageCache[productUrl] = imageUrl;
            return imageUrl;
          }
        }

        return null;
      } catch (error) {
        return null;
      }
    }

    async fetchMissingProductImages(offerings) {
      const offeringsNeedingImages = offerings.filter(function(item) {
        return item.needsImageFetch && item.url;
      });

      if (offeringsNeedingImages.length === 0) return;

      const self = this;

      const fetchPromises = offeringsNeedingImages.map(async function(item) {
        const imageUrl = await self.fetchProductImage(item.url);
        if (imageUrl) {
          item.image_url = imageUrl;
          item.featured_image = imageUrl;
          item.needsImageFetch = false;
        }
        return { id: item.id, imageUrl: imageUrl };
      });

      await Promise.all(fetchPromises);
    }

    updateCardImage(itemId, imageUrl) {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;

      const favoriteBtn = section.querySelector('.favorite-btn[data-id="' + itemId + '"]');
      if (!favoriteBtn) return;

      const card = favoriteBtn.closest('.content-card');
      if (!card) return;

      const img = card.querySelector('.card-image img');
      if (img) {
        img.src = imageUrl;
        img.style.opacity = '0';
        img.onload = function() {
          img.style.transition = 'opacity 0.3s ease';
          img.style.opacity = '1';
        };
      }
    }

    stripHTML(html) {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      const text = tmp.textContent || tmp.innerText || "";
      return text.length > 150 ? text.substring(0, 150) + "..." : text;
    }

    // Drop the .myshopify.com origin from a URL so the card link stays relative
    // and follows the shop's custom domain. No-op on relative or external links.
    stripShopHost(url) {
      if (!url) return url;
      const i = url.indexOf(".myshopify.com/");
      return i === -1 ? url : url.slice(i + ".myshopify.com".length);
    }

    displayGridResults(filtered) {
      const container = document.getElementById("resultsContainer-" + this.sectionId);
      if (!container) return;

      var allItems = [];
      var totalItems = 0;
      const self = this;

      // v1.4.0: Use combined array (preserves API order) when available
      if (filtered.combined && filtered.combined.length > 0) {
        filtered.combined.forEach(function(item) {
          allItems.push(Object.assign({}, item));
        });
      } else {
        filtered.articles.forEach(function(article) {
          allItems.push(Object.assign({}, article, { content_type: "article" }));
        });

        filtered.offerings.forEach(function(offering) {
          allItems.push(Object.assign({}, offering, { content_type: "offering" }));
        });
      }

      allItems = this.applySorting(allItems);

      if (this.dataSource === "api") {
        if (this.state.totalItemsFromAPI !== undefined) {
          totalItems = this.state.totalItemsFromAPI;
        } else {
          totalItems = allItems.length;
        }
      } else {
        totalItems = allItems.length;
      }

      this.state.totalPages = Math.ceil(totalItems / this.state.itemsPerPage);

      var displayItems = [];

      if (this.dataSource === "api") {
        if (this.paginationType === "pages") {
          displayItems = allItems;
          this.state.allItems = allItems;
          this.state.hasMore = this.state.currentPage < this.state.totalPages;
        } else {
          if (this.state.currentPage === 1) {
            displayItems = allItems;
            this.state.allItems = allItems;
          } else {
            displayItems = this.state.allItems.concat(allItems);
            this.state.allItems = displayItems;
          }
          this.state.hasMore = this.state.currentPage * this.state.itemsPerPage < totalItems;
        }
      } else {
        if (this.paginationType === "pages") {
          const start = (this.state.currentPage - 1) * this.state.itemsPerPage;
          const end = start + this.state.itemsPerPage;
          displayItems = allItems.slice(start, end);
          this.state.allItems = displayItems;
          this.state.hasMore = this.state.currentPage < this.state.totalPages;
        } else {
          if (this.state.currentPage === 1) {
            displayItems = allItems.slice(0, this.state.itemsPerPage);
            this.state.allItems = displayItems;
          } else {
            const start = (this.state.currentPage - 1) * this.state.itemsPerPage;
            const end = start + this.state.itemsPerPage;
            const newItems = allItems.slice(start, end);
            displayItems = this.state.allItems.concat(newItems);
            this.state.allItems = displayItems;
          }
          this.state.hasMore = this.state.currentPage * this.state.itemsPerPage < totalItems;
        }
      }

      var currentSortLabel = SORT_LABELS_DESKTOP[this.state.sortBy] || "Recommended";

      var html =
        '<div class="results-header">' +
        '<div class="sort-dropdown-wrapper">' +
        '<button class="sort-button" type="button">' +
        '<span>Sort by : <span class="current-sort">' + currentSortLabel + '</span></span>' +
        '<svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
        '</button>' +
        '<div class="sort-dropdown" style="display: none;">' +
        '<button class="sort-option' + (this.state.sortBy === "recommended" ? " active" : "") + '" data-sort="recommended" type="button">Recommended</button>' +
        '<button class="sort-option' + (this.state.sortBy === "newest" ? " active" : "") + '" data-sort="newest" type="button">Newest</button>' +
        '<button class="sort-option' + (this.state.sortBy === "oldest" ? " active" : "") + '" data-sort="oldest" type="button">Oldest</button>' +
        '<button class="sort-option' + (this.state.sortBy === "a-z" ? " active" : "") + '" data-sort="a-z" type="button">Alphabetical (A-Z)</button>' +
        '<button class="sort-option' + (this.state.sortBy === "z-a" ? " active" : "") + '" data-sort="z-a" type="button">Alphabetical (Z-A)</button>' +
        '</div>' +
        '</div>' +
        '</div>';

      if (displayItems.length === 0) {
        html += '<div class="empty-state"><p>No results found. Try adjusting your filters.</p></div>';
      } else {
        var cardsHtml = "";
        displayItems.forEach(function(item) {
          cardsHtml += self.renderCard(item);
        });
        html += '<div class="content-grid">' + cardsHtml + '</div>';

        if (this.paginationType === "button") {
          if (this.state.hasMore) {
            html += '<div class="load-more-container"><button class="load-more-btn" type="button">Load More</button></div>';
          } else if (displayItems.length > this.state.itemsPerPage) {
            html += '<div class="load-more-container"><p style="color: #666;">You\'ve reached the end</p></div>';
          }
        } else if (this.paginationType === "auto") {
          if (!this.state.hasMore && displayItems.length > this.state.itemsPerPage) {
            html += '<div class="load-more-container"><p style="color: #666;">You\'ve reached the end</p></div>';
          }
        } else if (this.paginationType === "pages") {
          html += this.renderPagination(totalItems);
        }
      }

      container.innerHTML = html;
      this.bindSortingEvents();
    }

    renderPagination(totalItems) {
      const totalPages = this.state.totalPages;
      const currentPage = this.state.currentPage;

      if (totalPages <= 1) return "";

      const start = (currentPage - 1) * this.state.itemsPerPage + 1;
      const end = Math.min(currentPage * this.state.itemsPerPage, totalItems);

      var paginationHTML =
        '<div class="pagination-info">Showing ' + start + '-' + end + ' of ' + totalItems + ' items</div>' +
        '<div class="pagination-container">';

      paginationHTML +=
        '<button class="pagination-btn pagination-prev" data-page="' + (currentPage - 1) + '" ' +
        (currentPage === 1 ? "disabled" : "") +
        ' type="button" aria-label="Previous page">' +
        '<svg width="16" height="16" viewBox="0 0 16 16" fill="none">' +
        '<path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
        '</svg>' +
        '</button>';

      const pageNumbers = this.getPageNumbers(currentPage, totalPages);

      pageNumbers.forEach(function(page) {
        if (page === "...") {
          paginationHTML += '<span class="pagination-ellipsis">...</span>';
        } else {
          paginationHTML +=
            '<button class="pagination-btn ' + (page === currentPage ? "active" : "") + '" ' +
            'data-page="' + page + '" type="button" aria-label="Page ' + page + '">' +
            page +
            '</button>';
        }
      });

      paginationHTML +=
        '<button class="pagination-btn pagination-next" data-page="' + (currentPage + 1) + '" ' +
        (currentPage === totalPages ? "disabled" : "") +
        ' type="button" aria-label="Next page">' +
        '<svg width="16" height="16" viewBox="0 0 16 16" fill="none">' +
        '<path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
        '</svg>' +
        '</button>';

      paginationHTML += "</div>";

      return paginationHTML;
    }

    getPageNumbers(current, total) {
      var pages = [];
      const delta = 2;

      if (total <= 7) {
        for (var i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        const rangeStart = Math.max(2, current - delta);
        const rangeEnd = Math.min(total - 1, current + delta);

        if (rangeStart > 2) pages.push("...");
        for (var j = rangeStart; j <= rangeEnd; j++) {
          pages.push(j);
        }
        if (rangeEnd < total - 1) pages.push("...");
        pages.push(total);
      }

      return pages;
    }

    goToPage(pageNum) {
      if (pageNum < 1 || pageNum > this.state.totalPages) return;
      if (pageNum === this.state.currentPage) return;

      this.state.currentPage = pageNum;
      this.renderResults();

      const container = document.getElementById("resultsContainer-" + this.sectionId);
      if (container) {
        const rect = container.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetY = rect.top + scrollTop - 100;
        window.scrollTo({ top: targetY, behavior: "smooth" });
      }
    }

    async loadMore() {
      if (this.state.isLoading || !this.state.hasMore) return;

      this.state.isLoading = true;
      this.state.currentPage++;

      const loadMoreContainer = document.querySelector('[data-section-id="' + this.sectionId + '"] .load-more-container');
      if (loadMoreContainer) {
        loadMoreContainer.innerHTML =
          '<div class="loading-more" style="display: flex; flex-direction: column; align-items: center; gap: 12px;">' +
          '<div class="spinner"></div>' +
          '<span style="color: #666;">Loading more...</span>' +
          '</div>';
      }

      await this.renderResults();
      this.state.isLoading = false;
    }

    displaySliderResults(filtered) {
      const container = document.getElementById("resultsContainer-" + this.sectionId);
      if (!container) return;

      var html = "";
      const itemsPerCategory = this.settings.itemsPerCategory || this.settings.items_per_category || 10;

      var groupedByCategory = {};
      const self = this;

      // v1.5.0: Group articles into every matching selected category slot
      // (an article with multiple tags appears in each matching category slider)
      filtered.articles.forEach(function(article) {
        const primaryId = article.category?.id || article.category;
        const allIds = [primaryId].concat(article.categories || []).filter(Boolean);
        const targetIds = allIds.filter(function(id) {
          return id === self.state.selectedL1 || self.state.selectedL2.includes(id);
        });
        const uniqueTargets = targetIds.filter(function(id, idx) {
          return targetIds.indexOf(id) === idx;
        });
        uniqueTargets.forEach(function(catId) {
          if (!groupedByCategory[catId]) groupedByCategory[catId] = [];
          groupedByCategory[catId].push(Object.assign({}, article, { content_type: "article" }));
        });
      });

      // v1.5.0: Same for offerings
      filtered.offerings.forEach(function(offering) {
        const primaryId = offering.category?.id || offering.category;
        const allIds = [primaryId].concat(offering.categories || []).filter(Boolean);
        const targetIds = allIds.filter(function(id) {
          return id === self.state.selectedL1 || self.state.selectedL2.includes(id);
        });
        const uniqueTargets = targetIds.filter(function(id, idx) {
          return targetIds.indexOf(id) === idx;
        });
        uniqueTargets.forEach(function(catId) {
          if (!groupedByCategory[catId]) groupedByCategory[catId] = [];
          groupedByCategory[catId].push(Object.assign({}, offering, { content_type: "offering" }));
        });
      });

      if (this.state.selectedL1 && groupedByCategory[this.state.selectedL1]) {
        const l1Cat = this.categoryMap.byId[this.state.selectedL1];
        const l1Items = groupedByCategory[this.state.selectedL1];
        const sortedItems = this.applySorting(l1Items);
        const displayItems = sortedItems.slice(0, itemsPerCategory);
        const hasMore = l1Items.length > itemsPerCategory;
        html += this.renderSection(l1Cat?.title || "", displayItems, this.state.selectedL1, hasMore);
      }

      this.state.selectedL2.forEach(function(l2Id) {
        if (groupedByCategory[l2Id]) {
          const l2Cat = self.categoryMap.byId[l2Id];
          const allItems = groupedByCategory[l2Id];
          const sortedItems = self.applySorting(allItems);
          const displayItems = sortedItems.slice(0, itemsPerCategory);
          const hasMore = allItems.length > itemsPerCategory;
          html += self.renderSection(l2Cat?.title || "", displayItems, l2Id, hasMore);
        }
      });

      if (!html) {
        if (this.state.selectedL1 && this.state.selectedL2.length === 0) {
          html = '<div class="empty-state"><p>No content found for this category.</p></div>';
        } else {
          html = '<div class="empty-state"><p>No results found. Try adjusting your filters.</p></div>';
        }
      }

      container.innerHTML = html;
      this.initializeSliders();
    }

    renderSection(title, items, categoryId, hasMore) {
      const sliderId = this.generateSlug(title) + "-" + this.sectionId;
      const l2Cat = this.categoryMap.byId[categoryId];
      const self = this;

      var cardsHtml = "";
      items.forEach(function(item) {
        cardsHtml += self.renderCard(item);
      });

      return (
        '<section class="content-section">' +
        '<div class="section-header">' +
        '<h2>' + title + '</h2>' +
        '<div class="slider-controls">' +
        '<button class="slider-prev" data-slider="' + sliderId + '" type="button" aria-label="Previous">' +
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none">' +
        '<path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
        '</button>' +
        '<button class="slider-next" data-slider="' + sliderId + '" type="button" aria-label="Next">' +
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none">' +
        '<path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
        '</button>' +
        '</div>' +
        '</div>' +
        '<div class="content-slider" data-slider="' + sliderId + '">' +
        '<div class="slider-track">' + cardsHtml + '</div>' +
        '</div>' +
        (hasMore
          ? '<a href="/pages/life-journey?category=' +
            (this.categoryMap.byId[this.state.selectedL1]?.slug || "") +
            '&subcategories=' +
            (l2Cat?.slug || "") +
            '" class="see-more-link">See more →</a>'
          : "") +
        '</section>'
      );
    }

    escapeAttr(value) {
      if (!value) return "";
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    }

    renderCard(item) {
      const type = item.content_type || (item.price ? "offering" : "article");
      const itemUrl = item.url || "#";

      var imageUrl = this.resolveDisplayImage(item, type);

      const safeTitle = this.escapeAttr(item.title || "");
      const safeUrl = this.escapeAttr(itemUrl);
      const safeImage = this.escapeAttr(imageUrl);
      const apifavestate = item.is_favorite;
      var isFavorited = this.state.favorites.includes(item.id);
      if (apifavestate || isFavorited) {
        isFavorited = true;
      }
      const favoriteClasses = "favorite-btn" + (isFavorited ? " is-favorited" : "");

      const authorLogo = this.getAuthorLogo(item);
      const authorName = this.escapeAttr(item.author || item.company_name || (item.author && typeof item.author === 'object' ? item.author.name : null) || "Author");

      if (type === "article") {
        return (
          '<article class="content-card article-card">' +
          '<a href="' + safeUrl + '" class="card-link-wrapper">' +
          '<div class="card-image">' +
          '<img src="' + safeImage + '" alt="' + safeTitle + '" loading="lazy" ' +
          'data-fallback="' + this.escapeAttr(this.getArticlePlaceholderByCategory(item.category?.id || item.category) || '') + '" ' +
          'onerror="this.onerror=null; this.src=this.dataset.fallback || \'\';">' +
          '</div>' +
          '<div class="card-content">' +
          '<div class="company-badge">' +
          (authorLogo
            ? '<img src="' + this.escapeAttr(authorLogo) + '" alt="' + authorName + '">'
            : '<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#4A5568"/></svg>') +
          '<span>' + this.escapeAttr(typeof item.author === 'object' && item.author.name ? item.author.name : item.author || item.company_name || "Author") + '</span>' +
          '</div>' +
          '<h3>' + safeTitle + '</h3>' +
          '<div class="card-footer">' +
          '<span class="card-link-text">Read more →</span>' +
          '<div class="card-meta">' +
          '<span>' + (item.read_time || 5) + ' mins read</span>' +
          '</div>' +
          '</div>' +
          '</div>' +
          '</a>' +
          '<button class="' + favoriteClasses + ' favorite-icon" type="button" aria-label="' + (isFavorited ? "Remove from favorites" : "Add to favorites") + '" aria-pressed="' + (isFavorited ? "true" : "false") + '" data-id="' + this.escapeAttr(item.id) + '" data-title="' + safeTitle + '" data-url="' + safeUrl + '" data-image="' + safeImage + '" data-type="article">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="' + (isFavorited ? "currentColor" : "none") + '" stroke="currentColor" stroke-width="2">' +
          '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>' +
          '</svg>' +
          '</button>' +
          '</article>'
        );
      }

      return (
        '<article class="content-card offering-card">' +
        '<a href="' + safeUrl + '" class="card-link-wrapper">' +
        '<div class="card-image">' +
        '<div class="offering-card__bg-image" style="background-image: url(\'' + safeImage + '\');" role="img" aria-label="' + safeTitle + '"></div>' +
        '</div>' +
        '<div class="card-content">' +
        '<h3>' + safeTitle + '</h3>' +
        '<p class="card-description">' + this.truncateText(item.description || item.summary || "", 100) + '</p>' +
        '<div class="card-footer">' +
        '<span class="card-link-text card-link-primary">Explore offering →</span>' +
        '</div>' +
        '</div>' +
        '</a>' +
        '<button class="' + favoriteClasses + ' favorite-icon" type="button" aria-label="' + (isFavorited ? "Remove from favorites" : "Add to favorites") + '" aria-pressed="' + (isFavorited ? "true" : "false") + '" data-id="' + this.escapeAttr(item.id) + '" data-title="' + safeTitle + '" data-url="' + safeUrl + '" data-image="' + safeImage + '" data-type="offering">' +
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="' + (isFavorited ? "currentColor" : "none") + '" stroke="currentColor" stroke-width="2">' +
        '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>' +
        '</svg>' +
        '</button>' +
        '</article>'
      );
    }

    truncateText(text, maxLength) {
      if (!text) return "";
      const stripped = text.replace(/<[^>]*>/g, "").trim();
      return stripped.length > maxLength ? stripped.substring(0, maxLength) + "..." : stripped;
    }

    initializeSliders() {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;

      section.querySelectorAll(".content-slider").forEach(function(slider) {
        const sliderId = slider.dataset.slider;
        const track = slider.querySelector(".slider-track");
        const prevBtn = section.querySelector('.slider-prev[data-slider="' + sliderId + '"]');
        const nextBtn = section.querySelector('.slider-next[data-slider="' + sliderId + '"]');

        if (!track || !prevBtn || !nextBtn) return;

        var currentPosition = 0;
        const cardWidth = 364;
        const visibleCards = 3;
        const totalCards = track.children.length;
        const maxScroll = Math.max(0, totalCards - visibleCards);

        var updateButtons = function() {
          prevBtn.disabled = currentPosition === 0;
          nextBtn.disabled = currentPosition >= maxScroll;
          prevBtn.style.opacity = currentPosition === 0 ? "0.3" : "1";
          nextBtn.style.opacity = currentPosition >= maxScroll ? "0.3" : "1";
        };

        prevBtn.addEventListener("click", function() {
          if (currentPosition > 0) {
            currentPosition--;
            track.style.transform = "translateX(-" + currentPosition * cardWidth + "px)";
            updateButtons();
          }
        });

        nextBtn.addEventListener("click", function() {
          if (currentPosition < maxScroll) {
            currentPosition++;
            track.style.transform = "translateX(-" + currentPosition * cardWidth + "px)";
            updateButtons();
          }
        });

        updateButtons();
      });
    }

    updateGradient(categoryId) {
      const section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;
      
      const lifeJourneySection = section.classList.contains('life-journey-section') 
        ? section 
        : section.querySelector('.life-journey-section');
        
      if (!lifeJourneySection) return;
      
      const gradientMap = {
        'Emotional Wellness': { start: '#FDEED2', end: '#FEFCF6' },
        'Financial & Legal Wellness': { start: '#F5D8D4', end: '#FEFCF6' },
      };
      
      let gradient = { start: '#faf8f5', end: '#FEFCF6' };
      
      if (categoryId) {
        const category = this.categoryMap.byId[categoryId];
        
        if (category) {
          if (category.level === 'l2') {
            const l1Parent = this.getL1Parent(categoryId);
            if (l1Parent && gradientMap[l1Parent.title]) {
              gradient = gradientMap[l1Parent.title];
            }
          } else if (category.level === 'l1') {
            if (gradientMap[category.title]) {
              gradient = gradientMap[category.title];
            }
          }
        }
      }
      
      lifeJourneySection.style.setProperty('--gradient-start', gradient.start);
      lifeJourneySection.style.setProperty('--gradient-end', gradient.end);
    }
  }

  window.LifeJourneyFilter = LifeJourneyFilter;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeAllFilters);
  } else {
    initializeAllFilters();
  }

  function initializeAllFilters() {
    if (!window.lifeJourneyData) return;
    document.querySelectorAll(".life-journey-section").forEach(function(section) {
      const sectionId = section.dataset.sectionId;
      if (sectionId && window.lifeJourneyData[sectionId]) {
        new LifeJourneyFilter(sectionId);
      }
    });
  }
})();
